import {useMedia} from "@hooks/ui/useMedia";
import React, {ReactElement, ReactNode} from 'react'
import {Box, Paper, Typography} from "@mui/material";
import {modalSx} from "./sx";
import ToggleFullScreen from "@ui/modal/ToggleFullScreen";
import IconButtonCustom from "@ui/buttons/iconButtonCustom/IconButtonCustom";
import {FullScreenIconOff, FullScreenIconOn} from "@ui/icons/icons";
import './styles.scss'
import {useThemeContext} from "@view/theme/context/hooks/useThemeContext";
import CloseIcon from '@mui/icons-material/Close';
import BaseModal from "@ui/modal/BaseModal";
import {SxType} from "@baseTypes/sx/sxTypes";


interface IProps {
    button: ReactElement,
    label?: string,
    initialFullScreenMode?: boolean,
    initialOpen?: boolean,
    empty?: boolean,
    // windows that size themselves only lose from a full screen toggle
    hideFullScreenToggle?: boolean,
    // lets a window repaint its own title bar without touching every other modal
    headerSx?: SxType,
    closeMenuFn?: () => void,
    onOpen?: () => void,
    onClose?: () => void,
    children?: ReactNode,

}

const ModalComponent = React.forwardRef(({
                                             children,
                                             button,
                                             label,
                                             initialFullScreenMode = false,
                                             initialOpen = false,
                                             empty = false,
                                             hideFullScreenToggle = false,
                                             headerSx,
                                             onOpen,
                                             onClose,
                                         }: IProps, ref) => {

    const {isDesktop} = useMedia()
    const {theme} = useThemeContext()

    const [open, setOpen] = React.useState(initialOpen);
    const [fullScreenMode, setFullScreenMode] = React.useState(!isDesktop || initialFullScreenMode);

    const handleOpen = () => {

        setOpen(true)
        if (onOpen) {
            onOpen()
        }
    };
    const handleClose = () => {
        setOpen(false)
        onClose && onClose()
    };


    React.useImperativeHandle(ref, () => ({
        closeModal() {
            handleClose();
        },
        openModal() {
            handleOpen();
        },

    }), []);


    if (empty) {
        return (
            <React.Fragment>
                {React.cloneElement(button, {
                    onClick: () => {
                        handleOpen();
                    },

                })}

                {open && <BaseModal
                    open={open}
                    onClose={handleClose}
                >
                    <Box
                        className={theme === 'dark' ? 'darkTheme' : 'lightTheme'}
                    >
                        {children}
                    </Box>
                </BaseModal>}

            </React.Fragment>
        )
    }

    return <>
        {React.cloneElement(button, {
            onClick: () => {
                handleOpen();
            },

        })}

        {open && <BaseModal
            open={open}
            onClose={handleClose}
        >
            <Paper
                sx={modalSx.root}
                className={theme === 'dark' ? 'darkTheme' : 'lightTheme'}
            >
                <ToggleFullScreen fullScreenMode={fullScreenMode}>

                    <Box sx={{...modalSx.info, ...headerSx}}>
                        <Typography
                            sx={modalSx.info.title}
                            variant={'h6'}>
                            {label}
                        </Typography>

                        <Box sx={modalSx.info.action}>
                            {isDesktop && !hideFullScreenToggle && <IconButtonCustom
                                onClick={() => setFullScreenMode((prev) => !prev)}
                            >
                                {fullScreenMode ? <FullScreenIconOn/> : <FullScreenIconOff/>}
                            </IconButtonCustom>}

                            <IconButtonCustom
                                onClick={handleClose}
                            >
                                <CloseIcon sx={modalSx.close}/>
                            </IconButtonCustom>
                        </Box>

                    </Box>


                    <Box sx={modalSx.content}>
                        <Box sx={modalSx.content.inner}>
                            {children}
                        </Box>
                    </Box>
                </ToggleFullScreen>
            </Paper>
        </BaseModal>}

    </>
});


export default ModalComponent;
